import { createKratosBrowserClient, decodeDiscordAuthorizationUrl } from "./kratos-browser-client.js";
import {
  DISCORD_LOGIN_REGISTRATION_GUARD_CONTROL,
  DISCORD_OIDC_CONSENT_CONTROL,
  identityBrowserContext,
  identityPopupContext,
} from "./kratos-browser-contract.js";
import {
  appendCurrentLegalAcceptance,
  prepareRegistrationLegalAcceptance,
} from "./legal-acceptance-contract.js";
import { currentAccountReturnPath } from "./account-route-contract.js";
import {
  clearIdentityPopupOperation,
  saveIdentityPopupOperation,
} from "./identity-popup-operation-repository.js";

const CHANNEL_PREFIX = "fishystuff:identity-provider:";
const COMPLETION_VERSION = 2;
const HANDOFF_VERSION = 1;
const PROVIDER_TIMEOUT_MS = 10 * 60 * 1000;
const COMPLETION_OPERATION_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const COMPLETION_OUTCOMES = new Set([
  "returned",
  "discordHandleUnavailable",
  "providerError",
]);
const PROVIDER_POPUP_FEATURES = "popup,width=720,height=900";
const PROVIDER_ACTIONS = Object.freeze({
  login: Object.freeze({ name: "provider", value: "discord" }),
  registration: Object.freeze({ name: "provider", value: "discord" }),
  settings: Object.freeze({ name: "link", value: "discord" }),
});

export class IdentityProviderPopupError extends Error {
  constructor(message, { kind = "unavailable" } = {}) {
    super(message);
    this.name = "IdentityProviderPopupError";
    this.kind = kind;
  }
}

export function identityProviderOutcomePresentation(kind) {
  const presentations = {
    discordHandleUnavailable: Object.freeze({
      key: "account.discord_handle_conflict.detail",
      fallback: "That Discord handle is unavailable. Sign in to link Discord to your account, or register manually with a different handle.",
    }),
    providerError: Object.freeze({
      key: "account.error.discord",
      fallback: "Discord sign-in could not be completed. Try again.",
    }),
    sessionUnavailable: Object.freeze({
      key: "account.error.discord_session",
      fallback: "Discord sign-in finished, but your FishyStuff account could not be loaded. Reload this page.",
    }),
  };
  return presentations[kind] ?? presentations.providerError;
}

function providerAction(view) {
  const action = PROVIDER_ACTIONS[view];
  if (!action) {
    throw new TypeError("Identity provider popup view is unsupported.");
  }
  return action;
}

export function isIdentityProviderSubmit(flow, submitter) {
  const action = PROVIDER_ACTIONS[flow?.view];
  return Boolean(
    action
    && submitter
    && submitter.name === action.name
    && submitter.value === action.value,
  );
}

function sourceValue(source, name) {
  const value = source?.get?.(name);
  return typeof value === "string" ? value : "";
}

export function providerPopupSubmission(
  flow,
  source,
  locale = globalThis.document?.documentElement?.lang === "de-DE" ? "de-DE" : "en-US",
) {
  const action = providerAction(flow?.view);
  const forms = flow?.forms?.filter(({ method }) => method === "oidc") ?? [];
  if (forms.length !== 1) {
    throw new IdentityProviderPopupError(
      "Discord is unavailable in this identity response.",
      { kind: "contract" },
    );
  }
  const submission = new URLSearchParams();
  for (const node of forms[0].nodes) {
    if (node.kind !== "input" || node.disabled) continue;
    if (node.name === action.name) {
      if (node.value !== action.value) {
        throw new IdentityProviderPopupError(
          "Discord action does not match this identity response.",
          { kind: "contract" },
        );
      }
      submission.set(node.name, node.value);
      continue;
    }
    if (node.type === "submit" || node.type === "button") continue;
    const supplied = sourceValue(source, node.name).trim();
    const value = supplied || node.value;
    if (node.name.startsWith("traits.") && !String(value).trim()) continue;
    submission.set(node.name, value);
  }
  if (flow.view === "login" && !flow.refresh) {
    submission.set(
      DISCORD_LOGIN_REGISTRATION_GUARD_CONTROL.name,
      DISCORD_LOGIN_REGISTRATION_GUARD_CONTROL.value,
    );
  } else if (flow.view === "registration") {
    if (flow.registrationEntry === "direct") {
      submission.set(
        DISCORD_OIDC_CONSENT_CONTROL.name,
        DISCORD_OIDC_CONSENT_CONTROL.value,
      );
    } else if (flow.registrationEntry !== "discordLoginFallback") {
      throw new IdentityProviderPopupError(
        "Discord registration source is unsupported.",
        { kind: "contract" },
      );
    }
    prepareRegistrationLegalAcceptance(source);
    appendCurrentLegalAcceptance(submission, locale);
  }
  return submission;
}

function completionMessage(value, operationId) {
  if (
    !value
    || typeof value !== "object"
    || Array.isArray(value)
    || value.version !== COMPLETION_VERSION
    || value.operationId !== operationId
    || !COMPLETION_OPERATION_ID_PATTERN.test(value.operationId)
    || !COMPLETION_OUTCOMES.has(value.outcome)
  ) {
    return null;
  }
  const expectedFields = ["operationId", "outcome", "version"];
  if (
    Object.keys(value).length !== expectedFields.length
    || Object.keys(value).some((key) => !expectedFields.includes(key))
  ) {
    return null;
  }
  return Object.freeze({ kind: value.outcome });
}

function handoffMessage(value, operationId) {
  if (!value || typeof value !== "object" || Array.isArray(value)
    || value.version !== HANDOFF_VERSION || value.operationId !== operationId
    || !COMPLETION_OPERATION_ID_PATTERN.test(operationId)) return null;
  const fields = value.action === "navigate"
    ? ["version", "operationId", "action", "redirectUrl"]
    : ["version", "operationId", "action"];
  if (!["ready", "navigate", "cancel"].includes(value.action)
    || Object.keys(value).length !== fields.length
    || Object.keys(value).some((key) => !fields.includes(key))
    || (value.action === "navigate" && typeof value.redirectUrl !== "string")) return null;
  return value;
}

function postHandoff(channel, operationId, action, redirectUrl) {
  channel.postMessage({
    version: HANDOFF_VERSION, operationId, action,
    ...(action === "navigate" ? { redirectUrl } : {}),
  });
}

// A COOP-isolated opener can lose its WindowProxy when this document loads.
// The waiting document owns its storage and navigation in its own context.
export function startIdentityProviderWaitingPopup({
  operationId,
  context,
  windowLike = globalThis.window,
  BroadcastChannelImpl = globalThis.BroadcastChannel,
  timeoutMs = PROVIDER_TIMEOUT_MS,
  setTimeoutImpl = globalThis.setTimeout,
  clearTimeoutImpl = globalThis.clearTimeout,
} = {}) {
  const channel = new BroadcastChannelImpl(`${CHANNEL_PREFIX}${operationId}`);
  let timeout;
  let disposed = false;
  const dispose = () => {
    if (disposed) return;
    disposed = true;
    clearTimeoutImpl(timeout);
    channel.removeEventListener("message", onMessage);
    windowLike.removeEventListener("pagehide", dispose);
    channel.close();
  };
  const cancel = () => {
    dispose();
    try { clearIdentityPopupOperation(windowLike); } finally { windowLike.close(); }
  };
  const onMessage = (event) => {
    const message = handoffMessage(event.data, operationId);
    if (!message || message.action === "ready") return;
    if (message.action === "cancel") {
      cancel();
      return;
    }
    try {
      const navigation = decodeDiscordAuthorizationUrl(message.redirectUrl, context);
      dispose();
      windowLike.location.replace(navigation.redirectUrl);
    } catch (error) {
      publishIdentityProviderCompletion({ operationId, outcome: "providerError", BroadcastChannelImpl });
      cancel();
      throw error;
    }
  };
  try {
    saveIdentityPopupOperation(windowLike, { operationId });
    channel.addEventListener("message", onMessage);
    windowLike.addEventListener("pagehide", dispose, { once: true });
    timeout = setTimeoutImpl(cancel, timeoutMs);
    postHandoff(channel, operationId, "ready");
  } catch (error) {
    dispose();
    publishIdentityProviderCompletion({ operationId, outcome: "providerError", BroadcastChannelImpl });
    windowLike.close();
    throw error;
  }
}

function waitForCompletion({
  channel,
  operationId,
  popup,
  signal,
  timeoutMs,
  setTimeoutImpl,
  clearTimeoutImpl,
}) {
  let cancel = () => {};
  let complete = () => {};
  let resolveReady;
  let rejectReady;
  const ready = new Promise((resolve, reject) => {
    resolveReady = resolve;
    rejectReady = reject;
  });
  void ready.catch(() => {});
  const promise = new Promise((resolve, reject) => {
    let settled = false;
    const finish = (callback, value) => {
      if (settled) return;
      settled = true;
      clearTimeoutImpl(timeout);
      signal?.removeEventListener?.("abort", onAbort);
      channel.removeEventListener?.("message", onMessage);
      channel.close();
      callback(value);
    };
    const onMessage = (event) => {
      if (handoffMessage(event?.data, operationId)?.action === "ready") {
        resolveReady();
        return;
      }
      const completion = completionMessage(event?.data, operationId);
      if (completion) {
        rejectReady(new IdentityProviderPopupError("Discord popup could not initialize."));
        finish(resolve, completion);
      }
    };
    const onAbort = () => {
      postHandoff(channel, operationId, "cancel");
      try { popup.close(); } catch (_error) { /* Window may already be detached. */ }
      const error = signal.reason instanceof Error
        ? signal.reason
        : new DOMException("Identity provider popup was cancelled.", "AbortError");
      rejectReady(error);
      finish(reject, error);
    };
    const timeout = setTimeoutImpl(() => {
      postHandoff(channel, operationId, "cancel");
      try { popup.close(); } catch (_error) { /* Window may already be detached. */ }
      const error = new IdentityProviderPopupError(
        "Discord did not return before the identity request expired.",
        { kind: "expired" },
      );
      rejectReady(error);
      finish(reject, error);
    }, timeoutMs);
    channel.addEventListener("message", onMessage);
    signal?.addEventListener?.("abort", onAbort, { once: true });
    cancel = (error) => {
      if (settled) return;
      postHandoff(channel, operationId, "cancel");
      rejectReady(error);
      finish(reject, error);
    };
    complete = () => {
      if (settled) return;
      postHandoff(channel, operationId, "cancel");
      finish(resolve, Object.freeze({ kind: "returned" }));
    };
    if (signal?.aborted) onAbort();
  });
  void promise.catch(() => {});
  return Object.freeze({
    promise, ready,
    complete: () => complete(),
    navigate: (redirectUrl) => postHandoff(channel, operationId, "navigate", redirectUrl),
    cancel: (error) => cancel(error),
  });
}

function identityProviderOperationId(cryptoLike) {
  if (typeof cryptoLike?.randomUUID !== "function") {
    throw new IdentityProviderPopupError("Discord popup support is unavailable.");
  }
  try {
    return cryptoLike.randomUUID();
  } catch (_error) {
    throw new IdentityProviderPopupError("Discord popup support is unavailable.");
  }
}


export async function startIdentityProviderPopup({
  view,
  mode = "default",
  sourceSubmission = new URLSearchParams(),
  apiBaseUrl,
  locationLike = globalThis.window?.location,
  windowLike = globalThis.window,
  BroadcastChannelImpl = globalThis.BroadcastChannel,
  cryptoLike = globalThis.crypto,
  clientFactory = createKratosBrowserClient,
  signal,
  timeoutMs = PROVIDER_TIMEOUT_MS,
  setTimeoutImpl = globalThis.setTimeout,
  clearTimeoutImpl = globalThis.clearTimeout,
} = {}) {
  if (
    typeof windowLike?.open !== "function"
    || typeof BroadcastChannelImpl !== "function"
  ) {
    throw new IdentityProviderPopupError("Discord popup support is unavailable.");
  }
  providerAction(view);
  const operationId = identityProviderOperationId(cryptoLike);
  const passkeyRpId = windowLike.__fishystuffRuntimeConfig?.identity?.passkeyRpId;
  const identityBaseUrl = windowLike.__fishystuffRuntimeConfig?.identity?.baseUrl;
  const context = identityPopupContext({
    apiBaseUrl,
    identityBaseUrl,
    locationLike,
    operationId,
    passkeyRpId,
  });
  const reauthenticationContext = identityBrowserContext({
    apiBaseUrl,
    identityBaseUrl,
    locationLike,
    passkeyRpId,
    returnPath: currentAccountReturnPath(locationLike),
  });
  const channel = new BroadcastChannelImpl(`${CHANNEL_PREFIX}${operationId}`);
  let popup;
  try {
    popup = windowLike.open(
      `${context.returnUrl}#waiting`,
      `fishystuff-identity-${operationId}`,
      PROVIDER_POPUP_FEATURES,
    );
  } catch (error) {
    channel.close();
    throw error;
  }
  if (!popup) {
    channel.close();
    throw new IdentityProviderPopupError(
      "Allow popups for FishyStuff to continue with Discord.",
      { kind: "blocked" },
    );
  }
  const completion = waitForCompletion({
    channel,
    operationId,
    popup,
    signal,
    timeoutMs,
    setTimeoutImpl,
    clearTimeoutImpl,
  });
  const cancelProvider = (reason = new DOMException(
    "Identity provider popup was cancelled.",
    "AbortError",
  )) => {
    completion.cancel(reason);
    void completion.promise.catch(() => {});
    try { popup.close(); } catch (_closeError) { /* Window may already be detached. */ }
  };
  try {
    await completion.ready;
    const client = clientFactory({ context });
    const started = await client.startFlow(view, { mode, signal });
    if (started.kind === "sessionAlreadyAvailable") {
      completion.complete();
      return await completion.promise;
    }
    if (started.kind !== "flow" || started.flow.view !== view) {
      throw new IdentityProviderPopupError(
        "Discord could not start from the current account state.",
        { kind: "state" },
      );
    }
    const submission = providerPopupSubmission(started.flow, sourceSubmission);
    const advance = async (advanceSignal) => {
      const continuation = await client.startProvider(
        started.flow,
        new URLSearchParams(submission),
        { signal: advanceSignal, reauthenticationContext },
      );
      if (continuation.kind === "reauthenticationRequired") {
        // The component may keep this capability while the user renews their
        // login. Its resume path still awaits and surfaces this same promise.
        void completion.promise.catch(() => {});
        return Object.freeze({
          ...continuation,
          cancel: cancelProvider,
          resume: async ({ signal: resumeSignal } = {}) => {
            const onAbort = () => cancelProvider(
              resumeSignal.reason instanceof Error
                ? resumeSignal.reason
                : new DOMException("Identity provider popup was cancelled.", "AbortError"),
            );
            resumeSignal?.addEventListener?.("abort", onAbort, { once: true });
            try {
              return await advance(resumeSignal);
            } catch (error) {
              cancelProvider(error);
              throw error;
            } finally {
              resumeSignal?.removeEventListener?.("abort", onAbort);
            }
          },
        });
      }
      if (continuation.kind !== "providerNavigation") {
        throw new IdentityProviderPopupError(
          "Discord did not return a provider navigation.",
          { kind: "contract" },
        );
      }
      completion.navigate(continuation.redirectUrl);
      return await completion.promise;
    };
    return await advance(signal);
  } catch (error) {
    cancelProvider(error);
    throw error;
  }
}

export function publishIdentityProviderCompletion({
  operationId,
  outcome,
  BroadcastChannelImpl = globalThis.BroadcastChannel,
} = {}) {
  if (typeof BroadcastChannelImpl !== "function") {
    throw new IdentityProviderPopupError("Identity completion channel is unavailable.");
  }
  const value = {
    version: COMPLETION_VERSION,
    operationId,
    outcome,
  };
  const message = completionMessage(value, operationId);
  if (!message) {
    throw new IdentityProviderPopupError(
      "Identity popup completion is invalid.",
      { kind: "contract" },
    );
  }
  const channel = new BroadcastChannelImpl(`${CHANNEL_PREFIX}${operationId}`);
  channel.postMessage(value);
  channel.close();
}
